import { ComponentType, ReactNode } from "react";

// DLS
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { ICornerRadius } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export interface GenericCroutonProps {
    title: string | ReactNode;
    subText?: string | ReactNode;
    leftIcon?: {
        icon?: ComponentType<unknown>;
        iconName?: IconName;
        strokeColor?: keyof IColors;
        fillColor?: keyof IColors;
    };
    icon?: string;
    preTextEl?: ReactNode;
    postTextEl?: ReactNode;
    durationOfCrouton?: number;
    postTextIcon?: string;
    cssProperties?: React.CSSProperties;
    closeIconCssProperties?: React.CSSProperties;
    wrapperClassName?: string;
    titleClassName?: string;
    subTextClassName?: string;
    buttonClassName?: string;
    iconClassName?: string;
    onClick?: VoidFunction;
    impressionEvent?: VoidFunction;
    setCroutonVisibility?: (croutonVisibility: boolean) => void;
    onClose?: VoidFunction;
    closeIcon?: ReactNode;
    showTimer?: boolean;
    duration?: number;
    onCompleteTimer?: () => void;
    onTimerLoad?: () => void;
    customWrapperDataTestId?: string;
    timerTextColor?: keyof IColors;
    textColor?: keyof IColors;
    backgroundColor?: keyof IColors;
    borderRadius?: keyof ICornerRadius;
    visuallyHidden?: boolean;
    // Absolute expiry used for more robust crouton timing (incl. AIP quick checkout),
    // so we don't need to maintain an explicit setTimeout.
    endTimestamp?: number;
}

export enum CroutonIds {
    ONE_BLACK_OTP = "ONE_BLACK_OTP",
    CBCC_NUDGE_CROUTON = "CBCC_NUDGE_CROUTON",
    COUPON_LISTING = "COUPON_LISTING",
    COUPONLESS_SAVINGS = "COUPONLESS_SAVINGS",
    DELIVERY_INSTRUCTION = "DELIVERY_INSTRUCTION",
    DELIVERY_SPEED = "DELIVERY_SPEED",
    CRAZY_DEAL_EXPIRY_TIMER = "CRAZY_DEAL_EXPIRY_TIMER",
    CRAZY_DEAL_EXPIRED = "CRAZY_DEAL_EXPIRED",
    IPL_SWIGGY_SIXES = "IPL_SWIGGY_SIXES",
    FREE_DEL_AND_CASHBACK_TOAST = "FREE_DEL_AND_CASHBACK_TOAST",
    DISCOUNTING_TOAST = "DISCOUNTING_TOAST",
    GIFTING_CART_ERROR = "GIFTING_CART_ERROR",
    FOOTER_CROUTON = "FOOTER_CROUTON",
    RX_CLOSING_TIMER = "RX_CLOSING_TIMER",
    TIMED_FREE_CASH_CROUTON = "TIMED_FREE_CASH_CROUTON",
    DEAL_RUSH = "DEAL_RUSH",
    MAGIC_ITEMS = "MAGIC_ITEMS",
    // MRO-specific: shows the magic-items countdown on the crouton instead of the cross-sell widget.
    MAGIC_ITEMS_TIMER_CROUTON = "MAGIC_ITEMS_TIMER",
    HOTEL_IN_ROOM_DELIVERY = "HOTEL_IN_ROOM_DELIVERY",
    DELIVERY_SPEED_TOING = "DELIVERY_SPEED_TOING",
}

export enum HalfcardIds {
    M2C_HALFCARD = "M2C_HALFCARD",
    FTUE_HALFCARD = "FTUE_HALFCARD",
    ADDRESS_LIST = "ADDRESS_LIST",
}

export enum CouponModalIds {
    AUTO_APPLIED_COUPON = "AUTO_APPLIED_COUPON",
    TRADE_DISCOUNT_COUPON = "TRADE_DISCOUNT_COUPON",
    INSTANT_COUPON = "INSTANT_COUPON",
    POCKET_HERO_CASHBACK = "POCKET_HERO",
    EXPIRY_CASHBACK = "EXPIRY_CASHBACK",
    GRATIFICATION_POPUP = "GRATIFICATION_POPUP",
    GIFTING_GRATIFICATION_POPUP = "GIFTING_GRATIFICATION_POPUP",
    TOING_AUTO_APPLIED_COUPON = "TOING_AUTO_APPLIED_COUPON",
    DEAL_RUSH = "DEAL_RUSH",
    GENERIC_DELIGHT_POPUP = "GENERIC_DELIGHT_POPUP",
    NO_FEE_WEEKEND_POPUP = "NO_FEE_WEEKEND_POPUP",
}

export enum LottieIds {
    CART_HEADER_LOTTIE = "CART_HEADER_LOTTIE",
    MAGIC_ITEMS_LOTTIE = "MAGIC_ITEMS_LOTTIE",
    SAVINGS_BANNER_ANIMATION = "SAVINGS_BANNER_ANIMATION",
    CART_SPLASH_LOTTIE = "CART_SPLASH_LOTTIE",
    TOING_HEADER_BANNER_ANIMATION = "TOING_HEADER_BANNER_ANIMATION",
    TOING_RTB_HEADER_LOTTIE = "TOING_RTB_HEADER_LOTTIE",
}

export enum CroutonPriority {
    PRIORITY_ORDER_ONE = 1,
    PRIORITY_ORDER_TWO = 2,
    PRIORITY_ORDER_THREE = 3,
    PRIORITY_ORDER_FOUR = 4,
}

export enum GlobalOverlayPriority {
    // QC → full-cart address list: must outrank all other cart overlays while open.
    ADDRESS_LIST = -1,
    NO_FEE_WEEKEND_POPUP = 0,
    GIFTING_GRATIFICATION_POPUP = 1,
    AUTO_APPLIED_COUPON = 1,
    TOING_AUTO_APPLIED_COUPON = 2,
    M2C = 2,
    M2C_FTUE = 3,
    GRATIFICATION_POPUP = 4,
    LOTTIE_ANIMATION = 5,
    SAVINGS_BANNER_ANIMATION = 6,
}

export interface CroutonOverlayProps {
    state: boolean;
    croutonContent: GenericCroutonProps | null;
}

export interface ShowCroutonArgs {
    forceRefreshContent?: boolean;
    content: GenericCroutonProps;
    priority: CroutonPriority | number;
    id: CroutonIds;
}

export enum OverlayType {
    MODAL = "MODAL",
    HALFCARD = "HALFCARD",
    LOTTIE = "LOTTIE",
    SAVINGS_ANIMATION = "SAVINGS_ANIMATION",
    CROUTON = "CROUTON",
}

export interface RegisterOverlaysArgs {
    overlayType: OverlayType;
    priority: GlobalOverlayPriority;
    id: HalfcardIds | CouponModalIds | LottieIds;
}

export interface OverlayQueue {
    [OverlayType.MODAL]: RegisterOverlaysArgs[];
    [OverlayType.HALFCARD]: RegisterOverlaysArgs[];
    [OverlayType.LOTTIE]: RegisterOverlaysArgs[];
    [OverlayType.SAVINGS_ANIMATION]: RegisterOverlaysArgs[];
    [OverlayType.CROUTON]: RegisterOverlaysArgs[];
}
